import { CURRENT_SERIES_ID } from '@/config/app.config';
import { read } from './http';
import { MOCK_FIXTURES } from './mock/fixtures.mock';
import type { Fixture } from '@/types';

/**
 * GET /api/v1/football/series/:seriesId/fixtures
 *
 * Upcoming and live fixtures by default; `includePast` adds finished ones.
 * `home` and `away` come back as resolved clubs, so no second lookup is needed.
 *
 * The parameter is **omitted** rather than sent as `false`: the API currently
 * treats any value of `includePast` as true, so `?includePast=false` returns
 * past fixtures too. Omitting it is the documented default and behaves
 * correctly. Reported to the API team — once fixed, the value can be sent
 * either way.
 */
export function getFixtures(
  includePast = false,
  signal?: AbortSignal,
): Promise<readonly Fixture[]> {
  const base = `/football/series/${CURRENT_SERIES_ID}/fixtures`;

  return read(
    includePast ? `${base}?includePast=true` : base,
    () => (includePast ? MOCK_FIXTURES : MOCK_FIXTURES.filter(isUpcoming)),
    signal,
  );
}

/** The fixture filter the API applies when `includePast` is left off. */
const isUpcoming = (fixture: Fixture): boolean =>
  new Date(fixture.startingAt).getTime() >= Date.now();

/** GET /api/v1/football/fixtures/:fixtureId */
export function getFixture(fixtureId: number, signal?: AbortSignal): Promise<Fixture> {
  return read(
    `/football/fixtures/${fixtureId}`,
    () => MOCK_FIXTURES.find((f) => f.fixtureId === fixtureId) ?? (MOCK_FIXTURES[0] as Fixture),
    signal,
  );
}
